import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ManualPage.css';
import DOMPurify from 'dompurify';
import html2pdf from 'html2pdf.js';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Extension, RawCommands } from '@tiptap/core';
import TextStyle from '@tiptap/extension-text-style';

// 1. 타입스크립트에게 새로운 명령어를 알려주기
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

// 2. 폰트 크기를 위한 커스텀 확장 기능 직접 만들기
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

// TipTap 에디터 컴포넌트
function TipTapEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle, // TextStyle은 FontSize를 위해 필요합니다.
      FontSize,  // 직접 만든 FontSize 확장 기능 사용
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
      },
    },
  });

  // 외부에서 value prop이 변경되었을 때 에디터 내용 동기화
  useEffect(() => {
    if (editor && editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);
  
  const handleFontSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = e.target.value;
    if (editor && size) {
      editor.chain().focus().setFontSize(size).run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div>
      <div className="editor-toolbar" style={{ marginBottom: 8, padding: 4, background: '#f5f5f5', borderRadius: 4, border: '1px solid #ddd', display: 'flex', gap: '8px' }}>
        <select onChange={handleFontSizeChange} value={editor.getAttributes('textStyle').fontSize || ''}>
          <option value="">글자 크기</option>
          {FONT_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
        <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}>굵게</button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}>기울임</button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}>취소선</button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

// flat 배열을 트리 구조로 변환
function flatToTree(nodes: any[]): any[] {
  const map: { [id: string]: any } = {};
  nodes.forEach(node => map[node.id] = { ...node, children: [] });
  const tree: any[] = [];
  nodes.forEach(node => {
    if (node.parentId) {
      map[node.parentId]?.children.push(map[node.id]);
    } else {
      tree.push(map[node.id]);
    }
  });
  // order 기준 정렬 (최상위 및 모든 children)
  function sortByOrder(arr: any[]) {
    arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    arr.forEach(n => n.children && sortByOrder(n.children));
  }
  sortByOrder(tree);
  return tree;
}

function findNodeByKey(nodes: any[], key: string): any {
  for (const node of nodes) {
    if (node.key === key || node.id === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
}
function getDepthByKey(nodes: any[], key: string, depth = 1): number | null {
  for (const node of nodes) {
    if (node.key === key || node.id === key) return depth;
    if (node.children) {
      const d = getDepthByKey(node.children, key, depth + 1);
      if (d) return d;
    }
  }
  return null;
}
function addNodeByKey(nodes, parentKey, newNode, maxDepth = 3, curDepth = 1) {
  return nodes.map(node => {
    if (node.key === parentKey && curDepth < maxDepth) {
      const children = node.children ? [...node.children, { ...newNode, children: [] }] : [{ ...newNode, children: [] }];
      return { ...node, children };
    } else if (node.children) {
      return { ...node, children: addNodeByKey(node.children, parentKey, newNode, maxDepth, curDepth + 1) };
    }
    return { ...node };
  });
}
function deleteNodeByKey(nodes, key) {
  return nodes
    .filter(node => node.key !== key)
    .map(node => node.children ? { ...node, children: deleteNodeByKey(node.children, key) } : { ...node });
}
// 검색어가 포함된 노드만 필터링 (부모/자식 포함)
function filterTree(nodes: any[], keyword: string): any[] {
  if (!keyword.trim()) return nodes;
  const lower = keyword.toLowerCase();
  function filter(nodes: any[]): any[] {
    return nodes
      .map(node => {
        const isMatch = (node.title || '').toLowerCase().includes(lower);
        if (isMatch) {
          // 부모가 매치되면 하위 전체를 모두 남김
          return { ...node };
        }
        // 자식 중 매치되는 게 있으면 그 자식만 남김
        const filteredChildren = node.children ? filter(node.children) : [];
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean);
  }
  return filter(nodes);
}
// 본문 검색 적용된 트리
function filterTreeByContent(nodes: any[], keyword: string): any[] {
  if (!keyword.trim()) return nodes;
  const lower = keyword.toLowerCase();
  function filter(nodes: any[]): any[] {
    return nodes
      .map(node => {
        const isMatch = (node.content || '').toLowerCase().includes(lower);
        if (isMatch) return { ...node };
        const filteredChildren = node.children ? filter(node.children) : [];
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean);
  }
  return filter(nodes);
}
// 검색 결과에 포함된 모든 key를 펼침(expanded) 대상으로 추출
function getAllKeys(nodes: any[]): string[] {
  let keys: string[] = [];
  nodes.forEach(node => {
    keys.push(node.key || node.id);
    if (node.children) keys = keys.concat(getAllKeys(node.children));
  });
  return keys;
}

// 트리에서 특정 노드의 내용을 업데이트하는 헬퍼 함수
function updateNodeInTree(nodes: any[], nodeId: string, newContent: string): any[] {
  return nodes.map(node => {
    if (node.id === nodeId) {
      return { ...node, content: newContent, updatedAt: new Date().toISOString() };
    }
    if (node.children) {
      return { ...node, children: updateNodeInTree(node.children, nodeId, newContent) };
    }
    return node;
  });
}

export default function ManualPage() {
  const [tree, setTree] = useState<any[]>([]); // 초기 빈 배열
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingRoot, setAddingRoot] = useState(false); // 루트 추가 모드
  const [editorValue, setEditorValue] = useState('');
  const [editingNode, setEditingNode] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const editorRef = useRef<any>(null);

  // 에디터 디폴트 안내문구 방지: 수정모드 진입 시 내용이 비어있으면 강제로 setHTML('')
  useEffect(() => {
    if (isEditMode && editingNode?.content === '') {
      setTimeout(() => {
        editorRef.current?.getInstance().setHTML('');
      }, 10);
    }
  }, [isEditMode, editingNode]);

  // 트리 전체 불러오기
  const fetchTree = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/manual/tree');
      if (!res.ok) throw new Error('트리 데이터를 불러오지 못했습니다');
      const data = await res.json();
      setTree(flatToTree(data));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  // 노드 선택 시 읽기모드로 전환, 에디터 값 초기화
  useEffect(() => {
    setIsEditMode(false);
    if (selectedKey) {
      const node = findNodeByKey(tree, selectedKey);
      setEditingNode(node);
      setEditorValue(node?.content || '');
    } else {
      setEditingNode(null);
      setEditorValue('');
    }
  }, [selectedKey, tree]);

  // 선택된 노드의 depth
  const selectedDepth = selectedKey ? getDepthByKey(tree, selectedKey) : null;

  // 검색어 적용된 트리 (제목)
  const filteredTree = useMemo(() => (search.trim() ? filterTree(tree, search) : tree), [tree, search]);
  
  // 본문 검색 적용된 트리
  const contentFilteredTree = useMemo(() => (contentSearch.trim() ? filterTreeByContent(filteredTree, contentSearch) : filteredTree), [filteredTree, contentSearch]);

  const autoExpanded = search.trim() || contentSearch.trim() ? getAllKeys(contentFilteredTree) : expanded;

  // 트리 재귀 렌더링
  function renderTree(nodes: any[], depth = 1, parentId: string | null = null) {
    return nodes.map((node, idx) => {
      const isExpandable = node.children && node.children.length > 0;
      const isOpen = autoExpanded.includes(node.key || node.id);
      const isSelected = selectedKey === (node.key || node.id);
      return (
        <div key={node.key || node.id}>
          <div
            className={`tree-item depth-${depth} ${isSelected ? 'selected' : ''}`}
            draggable={true}
            onDragStart={e => {
              e.dataTransfer.setData('draggedId', node.key || node.id);
              e.dataTransfer.setData('draggedIdx', idx.toString());
              e.dataTransfer.setData('draggedDepth', depth.toString());
              e.dataTransfer.setData('draggedParentId', (parentId || '').toString());
            }}
            onDragOver={e => {
              e.preventDefault();
            }}
            onDrop={e => {
              const draggedId = e.dataTransfer.getData('draggedId');
              const draggedIdx = Number(e.dataTransfer.getData('draggedIdx'));
              const draggedDepth = Number(e.dataTransfer.getData('draggedDepth'));
              const draggedParentId = e.dataTransfer.getData('draggedParentId') || '';
              const thisParentId = (parentId || '').toString();
              if (
                draggedId &&
                draggedId !== (node.key || node.id) &&
                draggedDepth === depth &&
                draggedParentId === thisParentId
              ) {
                const newNodes = [...nodes];
                const moving = newNodes.splice(draggedIdx, 1)[0];
                const dropIdx = idx > draggedIdx ? idx : idx;
                newNodes.splice(dropIdx, 0, moving);
                newNodes.forEach((n, i) => n.order = Date.now() + i);
                setTree(prev => {
                  function updateTree(tree: any[], curDepth = 1, curParentId: string | null = null) {
                    const curParent = (curParentId || '').toString();
                    if (curDepth === depth && curParent === thisParentId) return newNodes;
                    return tree.map(n => ({
                      ...n,
                      children: n.children ? updateTree(n.children, curDepth + 1, n.key || n.id) : n.children
                    }));
                  }
                  return updateTree(prev);
                });
                // 서버에 order 일괄 반영 (에러 처리 추가)
                const siblings = newNodes.map(n => ({ id: n.id, order: n.order }));
                fetch('/api/manual/batchOrder', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(siblings)
                }).catch(err => {
                  console.error('순서 변경 저장에 실패했습니다.', err);
                  alert('항목 순서 변경에 실패했습니다. 페이지를 새로고침 해주세요.');
                  // 에러 발생 시 원래 트리로 복구
                  fetchTree();
                });
              }
            }}
            onClick={e => { e.stopPropagation(); setSelectedKey(node.key || node.id); }}
          >
            {isExpandable && (
              <span
                className="tree-toggle"
                onClick={e => {
                  e.stopPropagation();
                  // 검색 중에도 폴더를 접고 펼 수 있도록 수정
                  // if (search.trim()) return;
                  setExpanded(prev =>
                    prev.includes(node.key || node.id)
                      ? prev.filter(k => k !== (node.key || node.id))
                      : [...prev, node.key || node.id]
                  );
                }}
              >
                {isOpen ? '▼' : '▶'}
              </span>
            )}
            <span className={`tree-label${isSelected ? ' selected' : ''}`}>{node.title}</span>
          </div>
          {isExpandable && isOpen && renderTree(node.children, depth + 1, node.key || node.id)}
        </div>
      );
    });
  }

  // 노드 추가 (선택된 노드 하위)
  function handleAdd() {
    setAddModal(true);
    setNewTitle('');
    setAddingRoot(false);
  }
  // 루트 노드 추가
  function handleAddRoot() {
    setAddModal(true);
    setNewTitle('');
    setAddingRoot(true);
  }
  async function handleAddOk() {
    if (addingRoot || tree.length === 0) {
      // 루트 노드 추가
      if (!newTitle.trim()) return setAddModal(false);
      setLoading(true);
      try {
        await fetch('/api/manual/node', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: newTitle,
            parentId: null,
            depth: 1,
            order: Date.now(),
            content: '',
            category: newTitle, // depth=1이면 카테고리명과 동일
            tags: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            history: [],
          })
        });
        setAddModal(false);
        setNewTitle('');
        setAddingRoot(false);
        await fetchTree();
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    // 기존 방식(자식 노드 추가)
    if (!selectedKey || !newTitle.trim() || selectedDepth == null || selectedDepth >= 3) return setAddModal(false);
    setLoading(true);
    try {
      // 부모 노드의 카테고리 상속(없으면 '')
      const parentNode = findNodeByKey(tree, selectedKey);
      await fetch('/api/manual/node', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle,
          parentId: selectedKey,
          depth: selectedDepth + 1,
          order: Date.now(),
          content: '',
          category: parentNode?.category || '',
          tags: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          history: [],
        })
      });
      setAddModal(false);
      setNewTitle('');
      await fetchTree();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  async function handleDelete() {
    if (!selectedKey) return;
    // 선택된 노드의 depth 확인
    const node = findNodeByKey(tree, selectedKey);
    const depth = node?.depth;
    let confirmMsg = '';
    if (depth === 1 || depth === 2) {
      confirmMsg = '하위 문서까지 모두 삭제합니다.\n그래도 삭제하시겠습니까?';
    } else if (depth === 3) {
      confirmMsg = '문서를 삭제합니다.\n삭제하시겠습니까?';
    } else {
      confirmMsg = '정말 삭제하시겠습니까?';
    }
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      await fetch(`/api/manual/node/${selectedKey}`, { method: 'DELETE' });
      setSelectedKey(null);
      await fetchTree();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // 저장 함수 (로컬 상태 우선 업데이트 - Optimistic Update)
  async function handleSaveContent() {
    if (!editingNode) return;
    setSaving(true);
    const originalContent = editingNode.content; // 롤백을 위한 원본 내용 저장
    
    try {
      const html = editorValue;
      const cleanHtml = DOMPurify.sanitize(html, { ALLOWED_TAGS: [
        'p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ol', 'ul', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'pre', 'code', 'blockquote', 'a', 'img'
      ], ALLOWED_ATTR: [
        'style', 'href', 'src', 'alt', 'title', 'target', 'colspan', 'rowspan'
      ] });
      
      const putBody: any = {
        id: editingNode.id,
        title: editingNode.title ?? '',
        parentId: editingNode.parentId ?? null,
        depth: editingNode.depth ?? 1,
        order: editingNode.order ?? Date.now(),
        createdAt: editingNode.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        content: cleanHtml ?? '',
        category: editingNode.category ?? '',
        tags: Array.isArray(editingNode.tags) ? editingNode.tags : [],
        history: Array.isArray(editingNode.history) ? editingNode.history : [],
      };

      // 1. 로컬 상태 즉시 업데이트
      setTree(prevTree => updateNodeInTree(prevTree, editingNode.id, cleanHtml));
      setIsEditMode(false);

      // 2. API 요청 보내기
      const res = await fetch(`/api/manual/node/${putBody.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(putBody),
      });

      // 3. API 응답 확인
      if (!res.ok) {
        throw new Error('서버 저장 실패');
      }

    } catch (e) {
      alert('저장 실패. 잠시 후 다시 시도해주세요.');
      // 실패 시 데이터 롤백
      setTree(prevTree => updateNodeInTree(prevTree, editingNode.id, originalContent));
    } finally {
      setSaving(false);
    }
  }

  // 취소 함수
  function handleCancelEdit() {
    setEditorValue(editingNode?.content || '');
    setIsEditMode(false);
  }

  // 트리 전체를 HTML로 변환 (계층 구조 반영)
  function treeToHtml(nodes, depth = 1) {
    return nodes.map(node => `
      <div style="margin-left:${(depth-1)*24}px; margin-bottom:12px;">
        <h${Math.min(depth+1, 6)}>${node.title}</h${Math.min(depth+1, 6)}>
        <div>${node.content || ''}</div>
        ${node.children && node.children.length > 0 ? treeToHtml(node.children, depth+1) : ''}
      </div>
    `).join('');
  }

  // PDF 내보내기 핸들러
  function handleExportPdf() {
    const html = `
      <div style="font-family: 'Malgun Gothic', '맑은 고딕', Arial, sans-serif;">
        <h1 style="text-align:center;">매뉴얼</h1>
        ${treeToHtml(tree)}
      </div>
    `;
    html2pdf().from(html).set({ filename: '매뉴얼.pdf', margin: 10, html2canvas: { scale: 1.2 } }).save();
  }

  return (
    <div className="manual-page">
      <div className="tree-panel">
        <div className="tree-toolbar">
          <button
            className="add-btn"
            onClick={handleAdd}
            disabled={tree.length > 0 ? (!selectedKey || (selectedDepth != null && selectedDepth >= 3)) : false}
          >
            추가
          </button>
          <button className="del-btn" onClick={handleDelete} disabled={!selectedKey}>삭제</button>
          <button
            className="add-btn"
            style={{ marginLeft: 8, background: '#1976d2', color: '#fff' }}
            onClick={handleAddRoot}
          >
            카테고리 추가
          </button>
        </div>
        <input
          className="tree-search"
          placeholder="매뉴얼 검색..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <input
          className="tree-search"
          placeholder="본문 검색..."
          value={contentSearch}
          onChange={e => setContentSearch(e.target.value)}
        />
        {/* PDF 내보내기 버튼 */}
        <button
          style={{ margin: '6px 0 10px 0', width: '40px', minWidth: 0, padding: '3px 0', borderRadius: 4, background: '#1976d2', color: '#fff', fontWeight: 500, fontSize: 13, border: 'none', cursor: 'pointer', letterSpacing: '0.5px', textAlign: 'center' }}
          onClick={handleExportPdf}
        >
          PDF
        </button>
        {loading && <div style={{ color: '#1976d2', margin: 8 }}>로딩 중...</div>}
        {error && <div style={{ color: 'red', margin: 8 }}>{error}</div>}
        {tree.length === 0 ? (
          <div style={{ margin: 24, color: '#888', textAlign: 'center' }}>
            <div>아직 매뉴얼 트리가 없습니다.</div>
            <button
              className="add-btn"
              style={{ marginTop: 16, padding: '8px 24px', fontSize: 16 }}
              onClick={handleAddRoot}
            >
              최상위 카테고리 추가
            </button>
          </div>
        ) : renderTree(contentFilteredTree)}
        {addModal && (
          <div className="modal-bg">
            <div className="modal">
              <div className="modal-title">새 항목 추가</div>
              <input
                className="modal-input"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddOk(); }}
                placeholder="제목 입력"
                autoFocus
              />
              <div className="modal-actions">
                <button onClick={() => { setAddModal(false); setAddingRoot(false); }}>취소</button>
                <button onClick={handleAddOk}>추가</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="detail-panel">
        {selectedKey ? (
          <>
            <h2>{editingNode?.title}</h2>
            {selectedDepth && selectedDepth > 1 ? (
              isEditMode ? (
                <>
                  <div style={{ marginBottom: 12, color: '#1976d2', fontWeight: 600, fontSize: 15 }}>
                    MS Word, Google Docs, 메모장 등에서 작성 후 <b>붙여넣기</b>를 권장합니다.
                  </div>
                  <div className="editor-area">
                    <TipTapEditor value={editorValue} onChange={setEditorValue} />
                  </div>
                  <div className="button-area">
                    <button onClick={handleSaveContent} disabled={saving} style={{ padding: '8px 24px', fontWeight: 600 }}>
                      {saving ? '저장 중...' : '저장'}
                    </button>
                    <button onClick={handleCancelEdit} disabled={saving} style={{ marginLeft: 8, padding: '8px 24px', fontWeight: 600 }}>
                      취소
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="editor-area">
                    <div
                      style={{ minHeight: 200, background: '#fff', border: '1px solid #eee', borderRadius: 6, padding: 18, marginBottom: 16 }}
                      dangerouslySetInnerHTML={{ __html: editingNode?.content || '<div style="color:#aaa">내용이 없습니다.</div>' }}
                    />
                  </div>
                  <div className="button-area">
                    <button onClick={() => { setIsEditMode(true); }} style={{ padding: '8px 24px', fontWeight: 600 }}>수정</button>
                  </div>
                </>
              )
            ) : null}
          </>
        ) : (
          <div style={{ color: '#aaa' }}>좌측에서 문서를 선택하세요.</div>
        )}
      </div>
    </div>
  );
} 